import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

const Sidebar = ({ collapsed = false, onToggle, onLogout }) => {
  const [expanded, setExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarExpanded');
      return saved ? JSON.parse(saved) : {
        balanceSheet: true,
        assets: true,
        liabilities: true,
        revolvingDebt: true,
        incomeStatement: true
      };
    } catch (e) {
      return {
        balanceSheet: true,
        assets: true,
        liabilities: true,
        revolvingDebt: true,
        incomeStatement: true
      };
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem('sidebarExpanded', JSON.stringify(expanded));
    } catch (e) { /* ignore */ }
  }, [expanded]);

  const toggleExpand = (section, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const navLinkClass = ({ isActive }) =>
    'sidebar-link' + (isActive ? ' active' : '');

  const renderToggle = (section) => (
    <button
      className="section-toggle-btn"
      onClick={(e) => toggleExpand(section, e)}
      aria-label={expanded[section] ? 'Collapse' : 'Expand'}
    >
      {expanded[section] ? '▾' : '▸'}
    </button>
  );

  return (
    <aside className="sidebar">
      <div className="sidebar-top">
        <div className="sidebar-header">Finanzen</div>
        <button
          className="sidebar-toggle"
          onClick={onToggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? '»' : '«'}
        </button>
      </div>

      <nav className="sidebar-section">
        <NavLink to="/" className={navLinkClass} end title="Home">
          <div className="sidebar-section-title">Home</div>
        </NavLink>
        <NavLink to="/bank-accounts" className={navLinkClass} title="Connect Bank">
          <div className="sidebar-section-title">Connect Bank</div>
        </NavLink>
      </nav>

      <nav className="sidebar-section">
        <div className="sidebar-title-wrapper">
          <NavLink to="/balance_sheet" className={navLinkClass} end title="Balance Sheet Overview">
            <div className="sidebar-section-title">Balance Sheet</div>
          </NavLink>
          {!collapsed && renderToggle('balanceSheet')}
        </div>
        
        {expanded.balanceSheet && (
          <>
            <div className="nav-sub">
              <div className="sidebar-title-wrapper">
                <NavLink to="/balance_sheet/assets" className={navLinkClass} title="Assets">
                  <span className="label">Assets</span>
                </NavLink>
                {!collapsed && renderToggle('assets')}
              </div>
              
              {expanded.assets && (
                <div className="nav-sub2">
                  <NavLink to="/balance_sheet/assets/cash_assets" className={navLinkClass} title="Cash Assets">
                    <span className="label">Cash Assets</span>
                  </NavLink>
                  <NavLink to="/balance_sheet/assets/invested_assets" className={navLinkClass} title="Invested Assets">
                    <span className="label">Invested Assets</span>
                  </NavLink>
                  <NavLink to="/balance_sheet/assets/physical_assets" className={navLinkClass} title="Physical Assets">
                    <span className="label">Physical Assets</span>
                  </NavLink>
                </div>
              )}
            </div>

            <div className="nav-sub">
              <div className="sidebar-title-wrapper">
                <NavLink to="/balance_sheet/financing" className={navLinkClass} title="Liabilities">
                  <span className="label">Liabilities</span>
                </NavLink>
                {!collapsed && renderToggle('liabilities')}
              </div>

              {expanded.liabilities && (
                <div className="nav-sub2">
                  <NavLink to="/balance_sheet/financing/auto_loans" className={navLinkClass} title="Auto Loans">
                    <span className="label">Auto Loans</span>
                  </NavLink>
                  <NavLink to="/balance_sheet/financing/mortgages" className={navLinkClass} title="Mortgages">
                    <span className="label">Mortgages</span>
                  </NavLink>
                  <NavLink to="/balance_sheet/financing/401k_loans" className={navLinkClass} title="401k Loans">
                    <span className="label">401k Loans</span>
                  </NavLink>
                  
                  <div className="sidebar-title-wrapper">
                    <NavLink to="/balance_sheet/financing/revolvers" className={navLinkClass} title="Revolving Debt">
                      <span className="label">Revolving Debt</span>
                    </NavLink>
                    {!collapsed && renderToggle('revolvingDebt')}
                  </div>

                  {expanded.revolvingDebt && (
                    <div className="nav-sub3">
                      <NavLink to="/balance_sheet/financing/revolvers/credit-cards/all" className={navLinkClass} title="Credit Cards">
                        <span className="label">Credit Cards</span>
                      </NavLink>
                      <NavLink to="/balance_sheet/financing/revolvers/helocs" className={navLinkClass} title="HELOCs">
                        <span className="label">HELOCs</span>
                      </NavLink>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="nav-sub">
              <NavLink to="/balance_sheet/equity" className={navLinkClass} title="Equity">
                <span className="label">Equity</span>
              </NavLink>
            </div>
          </>
        )}
      </nav>

      <nav className="sidebar-section">
        <div className="sidebar-title-wrapper">
          <NavLink to="/income_statement" className={navLinkClass} end title="Income Statement Overview">
            <div className="sidebar-section-title">Income Statement</div>
          </NavLink>
          {!collapsed && renderToggle('incomeStatement')}
        </div>

        {expanded.incomeStatement && (
          <div className="nav-sub">
            <NavLink to="/income_statement/income" className={navLinkClass} title="Income">
              <span className="label">Income</span>
            </NavLink>
            <NavLink to="/income_statement/expenses" className={navLinkClass} title="Expenses">
              <span className="label">Expenses</span>
            </NavLink>
            <NavLink to="/income_statement/budgets" className={navLinkClass} title="Budgets">
              <span className="label">Budgets</span>
            </NavLink>
          </div>
        )}
      </nav>
      
      <nav className="sidebar-section">
        <NavLink to="/cash_flow" className={navLinkClass} title="Cash Flow Statement">
          <div className="sidebar-section-title">Cash Flow Statement</div>
        </NavLink>
      </nav>

      <nav className="sidebar-section logout-section" style={{ marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '10px' }}>
        <button 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Logout button clicked - triggering onLogout');
            if (onLogout) {
                onLogout();
            } else {
                console.error('onLogout prop is missing in Sidebar');
            }
          }}
          className="sidebar-link logout-btn"
          style={{ 
            background: 'none', 
            border: 'none', 
            color: 'inherit', 
            width: '100%', 
            textAlign: 'left', 
            cursor: 'pointer',
            padding: '10px 15px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            zIndex: 1000
          }}
        >
          <span className="label" style={{ color: '#ff4d4d', fontWeight: 'bold' }}>Logout</span>
        </button>
      </nav>

    </aside>
  );
};

export default Sidebar;
